/**
 * Free-text DD/MM/YYYY date field. Native <input type="date"> can't be used
 * here: its displayed format follows the device's OS locale (verified —
 * Chromium reads LC_TIME at launch, ignoring the page's `lang` attribute,
 * `--lang` flag, and `navigator.language` alike), so it can't be forced to
 * DD/MM/YYYY for every user. This component owns its own digit mask instead
 * and still emits/accepts the same ISO (yyyy-mm-dd) shape the native input
 * did, so callers don't need to change their state.
 */
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

interface DateFieldDDMMYYYYProps {
  value: string
  onChange: (isoValue: string) => void
  min?: string
  required?: boolean
  inputStyle?: React.CSSProperties
  containerStyle?: React.CSSProperties
}

function digitsToDisplay(digits: string): string {
  return [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4, 8)].filter(Boolean).join('/')
}

function isoToDigits(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso)
  if (!m) return ''
  const [, y, mo, d] = m
  return d + mo + y
}

function daysInMonth(month: number, year: number): number {
  return new Date(year, month, 0).getDate()
}

function parseDigits(digits: string): { day: number; month: number; year: number } | null {
  if (digits.length !== 8) return null
  const day = Number(digits.slice(0, 2))
  const month = Number(digits.slice(2, 4))
  const year = Number(digits.slice(4, 8))
  if (year < 1000 || month < 1 || month > 12 || day < 1 || day > daysInMonth(month, year)) return null
  return { day, month, year }
}

function toISO(day: number, month: number, year: number): string {
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

export function DateFieldDDMMYYYY({ value, onChange, min, required, inputStyle, containerStyle }: DateFieldDDMMYYYYProps) {
  const { t } = useTranslation()
  const [digits, setDigits] = useState(() => isoToDigits(value))
  const [touched, setTouched] = useState(false)
  // Every value we hand back via onChange (including '' for an incomplete date) echoes
  // straight back down as the next `value` prop. Track what we last emitted so that echo
  // doesn't get mistaken for an external reset and wipe out digits the user is still typing.
  const lastEmitted = useRef(value)

  // Re-sync when the parent resets/prefills `value` from outside (e.g. editing an existing post)
  useEffect(() => {
    if (value === lastEmitted.current) return
    lastEmitted.current = value
    setDigits(isoToDigits(value))
  }, [value])

  const parsed = parseDigits(digits)
  const parsedISO = parsed ? toISO(parsed.day, parsed.month, parsed.year) : null
  const belowMin = !!(parsedISO && min && parsedISO < min)
  const incomplete = digits.length > 0 && digits.length < 8
  const invalid = digits.length === 8 && (!parsed || belowMin)
  const showError = touched && (incomplete || invalid || (!!required && digits.length === 0))

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const raw = e.target.value
    let nextDigits = raw.replace(/\D/g, '').slice(0, 8)
    // Backspacing over an auto-inserted "/" shrinks the raw value without
    // removing a digit — treat that the same as deleting the digit before it.
    if (raw.length < digitsToDisplay(digits).length && nextDigits === digits) {
      nextDigits = digits.slice(0, -1)
    }
    setDigits(nextDigits)
    const next = parseDigits(nextDigits)
    const nextISO = next ? toISO(next.day, next.month, next.year) : null
    const emitted = nextISO && !(min && nextISO < min) ? nextISO : ''
    lastEmitted.current = emitted
    onChange(emitted)
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', ...containerStyle }}>
      <input
        type="text"
        inputMode="numeric"
        dir="ltr"
        autoComplete="off"
        placeholder="DD/MM/YYYY"
        value={digitsToDisplay(digits)}
        onChange={handleChange}
        onBlur={() => setTouched(true)}
        style={{
          width: '100%',
          ...inputStyle,
          ...(showError ? { borderColor: 'var(--danger)' } : {}),
        }}
      />
      {showError && (
        <span style={{ display: 'block', marginTop: 4, fontSize: 'var(--fs-xs)', color: 'var(--danger)', fontFamily: 'var(--font-sans)' }}>
          {t('common.invalid_date')}
        </span>
      )}
    </div>
  )
}
